<template>
  <th v-if="isHeader">
    <!-- Table Header actions -->
    Actions
  </th>
  <td v-else>
    <!-- Table Body - Row actions -->
    <div>
      <loading v-if="isUpdating" />
      <dropdown v-else-if="$route.meta.isAdmin">
        <dropdown-action
          v-if="
            ['approved', 'rejected'].includes(rowData.status) === false &&
              userCanApprove &&
              !isDraft
          "
          success
          icon="tick"
          @click="onClickApprove"
          >Approve page</dropdown-action
        >
        <dropdown-action
          v-if="
            ['approved', 'rejected'].includes(rowData.status) === false &&
              userCanApprove &&
              !isDraft
          "
          danger
          icon="cross"
          @click="onClickReject"
          >Reject page</dropdown-action
        >
        <dropdown-action icon="preview" @click="onClickView" v-if="!isDraft"
          >View page</dropdown-action
        >
        <dropdown-action
          icon="preview"
          @click="onClickEdit"
          v-if="userCanAdd && !isDraft"
          >Edit page</dropdown-action
        >
        <dropdown-action
          icon="preview"
          @click="onClickEditDraft"
          v-if="userCanAdd && isDraft"
          >Edit draft</dropdown-action
        >
        <dropdown-action
          icon="preview"
          @click="onClickDuplicate"
          v-if="userCanAdd && !isDraft"
          >Duplicate page</dropdown-action
        >
        <dropdown-action
          danger
          icon="delete"
          @click="onClickDelete"
          v-if="userCanApprove && !isDraft"
          >Delete page</dropdown-action
        >
        <dropdown-action
          danger
          icon="delete"
          @click="onClickDeleteDraft"
          v-if="userCanApprove && isDraft"
          >Delete draft</dropdown-action
        >
      </dropdown>
    </div>
    <modal
      v-if="rejectModalOpen"
      class="ta-center"
      show-close-button
      @close="rejectModalOpen = false"
    >
      <h1 slot="header">Reason for Rejection</h1>
      <form method="post" action v-on:submit.prevent="rejectPage">
        <form-field
          type="textarea"
          placeholder="Please specify a reason for rejection"
          v-model="rejectionReason"
          required
        />
        <base-button type="submit" :primary="true">Submit Reason</base-button>
      </form>
    </modal>
  </td>
</template>

<script>
import Dropdown from './Dropdown'
import DropdownAction from './DropdownAction'

import VuetableFieldMixin from 'vuetable-2/src/components/VuetableFieldMixin'
import UserHasPermission from '@/mixins/UserHasPermission'

export default {
  name: 'page-actions',

  created() {
    const { tab } = this.$route.query
    this.isDraft = tab === 'drafts' || false
  },

  methods: {
    onClickApprove() {
      const pageId = this.rowData.page ? this.rowData.page.id : this.rowData.id
      /**
       * dependant on if we are a draft or not, we need to change what happens
       * If we have a draft, load the elements/data etc., update main article, approve, delete draft
       * If we don't have a draft, just update
       */
      const vm = this
      this.$store.dispatch('createDialog', {
        confirm: () => {
          vm.isUpdating = true;

          // check for a draft
          this.checkForDraft(pageId).then(() => {
            this.$store.dispatch('pages/approve', pageId).then((response) => {
              setTimeout(() => {
                vm.$store.dispatch('pages/list')
                vm.isUpdating = false;
              }, 500) //temporary measure for now
            })
          })

          // need to load draft elements, update page, approve
        },
      })
    },

    async checkForDraft(pageId) {
      let draft = null
      // if there is no draft, then return false to prevent ajax call
      if (!this.rowData.has_draft) {
        return false
      }
      try {
        draft = await this.$store.dispatch('pages/getDraft', { pageId })
      } catch (err) {
        // in case there is no draft
      }
      if (draft) {
        // TODO: Handle a page with no elements
        let pageElments = []
        try {
          pageElments = await this.$store.dispatch('pageElements/listDraft', {
            pageId,
            setState: false,
          })
        } catch (err) {
          // might be no page elements
        }
        const editedPage = JSON.parse(JSON.stringify(this.rowData.page))

        // also need to copy lots of other elements from the draft
        editedPage.title = draft.title
        editedPage.slug = draft.slug
        editedPage.review_date = draft.review_date
        editedPage.interestgroups = draft.interestgroups
        editedPage.parent_id = draft.parent_id
        editedPage.menu_sort = draft.menu_sort

        // add elements to the current page.
        editedPage.elements = pageElments

        // interest groups need to be just ids
        if (!editedPage.interestgroup_ids && editedPage.interestgroups) {
          editedPage.interestgroup_ids = editedPage.interestgroups.map(
            (group) => group.id
          )
        }
        // then remove the interest group bit
        delete editedPage.interestgroups

        await this.$store.dispatch('pages/update', {
          pageId: pageId,
          pageData: editedPage,
        })

        //TODO: Should this be done once the page is approved? Does it matter?
        try {
          await this.$store.dispatch('pages/deleteDraft', pageId)
        } catch (err) {
          // just in case there is no draft
        }

        return Promise.resolve()
      }

      // no draft, just resolve
      return Promise.resolve()
    },

    onClickReject() {
      this.rejectModalOpen = true
      return
    },

    rejectPage() {
      const vm = this
      const pageId = this.rowData.page ? this.rowData.page.id : this.rowData.id
      this.$store.dispatch('createDialog', {
        confirm: () => {
          this.$store
            .dispatch('pages/reject', {
              pageId: pageId,
              reason: this.rejectionReason,
            })
            .then((response) => {
              setTimeout(() => {
                vm.$store.dispatch(
                  'notifications/create',
                  {
                    body: `The page <b>${vm.rowData.title}</b> was rejected`,
                    type: 'success',
                  },
                  { root: true }
                )
                vm.$store.dispatch('pages/list')
              }, 500) //temporary measure for now
            })
        },
      })
    },

    onClickView() {
      if (!this.isDraft) {
        this.$router.push(`/content${this.rowData.page_url}`)
      }
    },

    onClickEdit() {
      const rowPageId = this.rowData.page
        ? this.rowData.page.id
        : this.rowData.id //now we're mixing data on views, checking for page id from properties, otherwise reverting to default resource id
      this.$router.push({
        name: 'AdminContentPageEdit',
        params: { pageId: rowPageId },
      })
    },

    onClickEditDraft() {
      const rowPageId = this.rowData.page
        ? this.rowData.page.id
        : this.rowData.id //now we're mixing data on views, checking for page id from properties, otherwise reverting to default resource id
      this.$router.push({
        name: 'AdminContentPageEdit',
        params: { pageId: rowPageId },
      })
    },

    onClickDuplicate() {
      const rowPageId = this.rowData.page
        ? this.rowData.page.id
        : this.rowData.id
      this.$router.push(`/admin/content/pages/create?duplicate=${rowPageId}`)
    },

    onClickDelete() {
      const vm = this
      const rowPageId = vm.rowData.page ? vm.rowData.page.id : vm.rowData.id //now we're mixing data on views, checking for page id from properties, otherwise reverting to default resource id
      vm.$store.dispatch('createDialog', {
        confirm: () => {
          vm.$store.dispatch('pages/delete', rowPageId).then((response) => {
            setTimeout(() => {
              vm.$store.dispatch('pages/list')
            }, 500) //temporary measure for now
          })
        },
      })
    },

    onClickDeleteDraft() {
      const vm = this
      // vm.rowData
      const rowPageId = vm.rowData.page ? vm.rowData.page.id : vm.rowData.id
      vm.$store.dispatch('createDialog', {
        confirm: () => {
          vm.$store
            .dispatch('pages/deleteDraft', rowPageId)
            .then((response) => {
              setTimeout(() => {
                vm.$store.dispatch('pages/list', { draft: true })
              }, 500) //temporary measure for now
            })
        },
      })
    },
  },

  computed: {
    userCanAdd() {
      return this.userHasPermission('add_edit_pages')
    },
    userCanApprove() {
      return this.userHasPermission('approve_pages')
    },
  },

  data() {
    return {
      rejectionReason: '',
      rejectModalOpen: false,
      isDraft: false,
      isUpdating: false
    }
  },

  components: {
    Dropdown,
    DropdownAction,
  },

  mixins: [VuetableFieldMixin, UserHasPermission],
}
</script>
